import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { differenceInHours, format } from 'date-fns';
import { useMutation } from '@apollo/client';
import ActionButtons from '../ActionButtons';
import Receipt from '@/shared/components/Receipt/Receipt';
import BackToTopButton from '@/shared/components/BackToTopButton';
import PaymentMethodDisplay from '@/shared/components/Receipt/PaymentMethodDisplay';
import styled from '@emotion/native';
import { BodyText, H3, BaseText } from '@/shared/components/StyledText';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import {
  Consultation,
  ConsultationStatus,
  SessionType,
} from '@/shared/types/generated';
import { sand, subheadline, green, mint, accent } from '@/shared/styles/colors';
import StatusBadge from '../StatusBadge';
import { Image } from 'react-native';
import {
  formatCurrency,
  getIsConsultationCancelled,
  formatNameWithLastInitial,
  getStylistRate,
} from '@/shared/utils';
import { ReceiptValue } from '@/shared/components/Receipt/ReceiptItem';
import { ReceiptIcon } from '@/shared/components/Receipt/ReceiptItem';
import { ReceiptItemProps } from '@/shared/components/Receipt/ReceiptItem';
import Spacer from '@/shared/components/Spacer';
import {
  DateAndTimeIcon,
  DurationIcon,
  InPersonIcon,
  ReasonIcon,
  StarIcon,
  StylistIcon,
  VirtualIcon,
} from '@/shared/components/Receipt/ReceiptIcons';
import useStore from '@/stores/useStore';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import CancelSessionModal from '../CancelSessionModal';
import CancelButton from './CancelButton';
import ComeBackText from './ComeBackText';
import DateText from './DateText';
import { ReceiptLabelText } from '@/shared/components/Receipt/constants';
import useSendbirdChannelUrl from '@/shared/hooks/useSendbirdChannelUrl';
import { useChannelRouting } from '@/shared/hooks/useChannelRouting';
import ModalWrapper from '@/shared/styles/components/ModalWrapper';
import { css } from '@emotion/native';
import { useJoinCall } from '@/shared/hooks/useJoinCall';
import SentryService from '@/services/sentry/Sentry.service';
import ViewPrivateNotesLink from '@/shared/components/ViewPrivateNotesLink';
import { PRICING_TIER_OPTIONS } from '@/shared/constants/instantCall';
import PromoCodeDisplay from '@/shared/components/Receipt/PromoCodeDisplay';
import Button from '@/shared/components/Button';
import {
  APPROVE_IN_PERSON_REQUEST,
  DECLINE_IN_PERSON_REQUEST,
} from '@/shared/mutations';
import {
  formatAddress,
  isInPersonSession,
  isInPersonAtEvent,
} from '@/shared/utils/sessionUtils';
import InPersonCompletionModal from '../InPersonCompletionModal';

const SessionDetailView = ({
  consultation,
  onClose,
  isSessionToday,
  isJoinable,
  openProfileModal,
  onRequestHandled,
}: {
  consultation: Consultation;
  onClose: () => void;
  isSessionToday: boolean;
  isJoinable: boolean;
  openProfileModal: () => void;
  onRequestHandled?: () => void;
}) => {
  const { isDesktop } = useBreakpoint();
  const { isStylist, setSnackbarState } = useStore();
  const { fetchChannelUrl } = useSendbirdChannelUrl();
  const { navigateToChannel } = useChannelRouting();
  const { handleJoinCall, isJoining } = useJoinCall({ consultation });

  const hoursUntilConsultation = differenceInHours(
    consultation.startTime,
    new Date(),
  );

  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [isProcessingRequest, setIsProcessingRequest] = useState(false);
  const [
    isInPersonCompletionModalVisible,
    setIsInPersonCompletionModalVisible,
  ] = useState(false);

  // Check if this is a pending in-person session request
  const isPendingInPersonRequest =
    consultation.status === ConsultationStatus.Pending &&
    consultation.sessionType === SessionType.InPersonSession;

  // Get client address for pending requests
  const clientAddress = consultation.client?.user?.address;
  const hasClientAddress = Boolean(clientAddress?.streetAddress);

  // Mutations for accept/decline
  const [approveRequest] = useMutation(APPROVE_IN_PERSON_REQUEST);
  const [declineRequest] = useMutation(DECLINE_IN_PERSON_REQUEST);

  const handleAcceptRequest = async () => {
    setIsProcessingRequest(true);
    try {
      await approveRequest({
        variables: {
          input: { consultationId: consultation.id },
        },
      });
      setSnackbarState({
        open: true,
        message: 'Request approved! Session has been confirmed.',
        severity: 'success',
      });
      onClose();
      onRequestHandled?.();
    } catch (error) {
      SentryService.captureError('Failed to approve in-person request', error);
      setSnackbarState({
        open: true,
        message: 'Failed to approve request. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsProcessingRequest(false);
    }
  };

  const handleDeclineRequest = async () => {
    setIsProcessingRequest(true);
    try {
      await declineRequest({
        variables: {
          input: { consultationId: consultation.id },
        },
      });
      setSnackbarState({
        open: true,
        message: 'Request declined.',
        severity: 'info',
      });
      onClose();
      onRequestHandled?.();
    } catch (error) {
      SentryService.captureError('Failed to decline in-person request', error);
      setSnackbarState({
        open: true,
        message: 'Failed to decline request. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsProcessingRequest(false);
    }
  };

  const handleGoToChat = async () => {
    if (!consultation.stylist?.user?.id || !consultation.client?.user?.id) {
      return;
    }
    const channelUrl = await fetchChannelUrl(
      consultation.stylist?.user?.id,
      consultation.client?.user?.id,
    );
    if (channelUrl) {
      navigateToChannel(channelUrl);
      onClose();
    }
  };

  const handleViewProfile = () => {
    onClose();
    openProfileModal();
  };

  const scrollToTop = useScrollToTop();
  const getAesthetics = (aesthetics: string[]) => {
    return aesthetics.length > 0 ? aesthetics.join(', ') : 'Missing aesthetics';
  };

  const priceTierLabel = PRICING_TIER_OPTIONS.find(
    (tier) => tier.value === consultation.stylist?.pricing?.tierName,
  )?.label;

  const stylistRate = getStylistRate(consultation);

  const inPersonSession = isInPersonSession(consultation);
  const inPersonAtEvent = isInPersonAtEvent(consultation);

  const handleEndSession = () => {
    setIsInPersonCompletionModalVisible(true);
  };

  const stylistReceiptItem: ReceiptItemProps = {
    label: ReceiptLabelText.Aesthetics,
    value: getAesthetics(consultation?.client?.aesthetics ?? []),

    icon: <StylistIcon />,
  };
  const hasDiscountCode =
    consultation.payment?.discountCode && consultation.payment.discountAmount;

  // Build session location value for in-person requests
  const sessionLocationValue =
    (isPendingInPersonRequest || inPersonSession) && clientAddress
      ? formatAddress(clientAddress)
      : '';

  const receiptItems: ReceiptItemProps[] = [
    {
      label: ReceiptLabelText.SessionType,
      value: inPersonSession ? 'In-Person' : 'Virtual',
      icon: inPersonSession ? <InPersonIcon /> : <VirtualIcon />,
    },
    // Add Session Location for in-person sessions (pending or confirmed)
    ...((isPendingInPersonRequest || inPersonSession) && hasClientAddress
      ? [
          {
            label: ReceiptLabelText.SessionLocation,
            value: sessionLocationValue,
          } as ReceiptItemProps,
        ]
      : []),
    // Add Access Details for in-person sessions if available
    ...((isPendingInPersonRequest || inPersonSession) &&
    clientAddress?.accessDetails
      ? [
          {
            label: ReceiptLabelText.AccessDetails,
            value: clientAddress.accessDetails,
          } as ReceiptItemProps,
        ]
      : []),
    {
      label: ReceiptLabelText.DateAndTime,
      value: `${format(consultation.startTime, 'MM/dd/yy')} at ${format(consultation.startTime, 'h:mmaaa')}`,
      icon: <DateAndTimeIcon />,
    },
    {
      label: ReceiptLabelText.Duration,
      value: `${consultation.estimatedDuration} mins`,
      icon: <DurationIcon />,
    },
    {
      label: ReceiptLabelText.Reason,
      value: consultation.consultationDetails?.reason ?? 'Missing reason',
      icon: <ReasonIcon />,
    },
    isStylist
      ? stylistReceiptItem
      : {
          label: ReceiptLabelText.Stylist,
          value: formatNameWithLastInitial(consultation?.stylist?.user),
          icon: <StylistIcon />,
        },
    ...(consultation.isInstant
      ? [
          {
            label: ReceiptLabelText.PriceTier,
            value: `${priceTierLabel} (${formatCurrency(stylistRate ?? 0)}/hr)`,
            icon: <StarIcon />,
          } as ReceiptItemProps,
        ]
      : []),
    ...(hasDiscountCode && consultation.payment?.discountCode
      ? [
          {
            label: ReceiptLabelText.PromoCode,
            customElement: (
              <PromoCodeDisplay
                onDiscountApplied={() => {}}
                onDiscountRemoved={() => {}}
                stylistId={consultation.stylist?.id ?? null}
                initialCodeText={consultation.payment?.discountCode?.codeText}
                initialDiscountPercentage={
                  consultation.payment?.discountCode?.discountPercentage
                }
                isReceiptItem
              />
            ),
          } as ReceiptItemProps,
        ]
      : []),
    {
      label: ReceiptLabelText.PaymentMethod,
      customElement: !isStylist ? (
        <PaymentMethodDisplay
          consultation={consultation}
          onError={(error) => {
            SentryService.captureError(
              'Failed to update payment method',
              error,
            );
          }}
        />
      ) : (
        <>
          <ReceiptIcon source={require('@/assets/images/Card.png')} />
          <ReceiptValue>••••</ReceiptValue>
        </>
      ),
    },
  ];

  const otherUser = isStylist
    ? consultation.client?.user
    : consultation.stylist?.user;

  const handleCancelPress = () => {
    setIsCancelModalVisible(true);
  };

  return (
    <ModalWrapper style={containerStyles} isScrollable>
      <Header>
        <HeaderLeft>
          {isPendingInPersonRequest ? (
            <PendingRequestBadge>Pending Request</PendingRequestBadge>
          ) : (
            <StatusBadge
              status={consultation.status}
              isSessionToday={isSessionToday}
              canJoin={isJoinable}
            />
          )}
        </HeaderLeft>
        <TouchableOpacity onPress={onClose}>
          <Image
            source={require('@/assets/images/CloseGreen.png')}
            style={{ width: 20, height: 20 }}
          />
        </TouchableOpacity>
      </Header>

      <Content>
        <Title>
          Session with {'\n'}
          {otherUser?.firstName} {otherUser?.lastName}
        </Title>
        <DateText
          startTime={consultation.startTime}
          cancelTime={
            consultation.status === ConsultationStatus.ClientNoShow ||
            consultation.status === ConsultationStatus.StylistNoShow
              ? consultation.startTime
              : consultation.cancelTime
          }
          consultationStatus={consultation.status}
        />
        {/* Address Banner for In-Person Sessions (Pending or Confirmed) - Stylist View */}
        {isStylist && (isPendingInPersonRequest || inPersonSession) && (
          <AddressBanner>
            <AddressBannerText>
              This styling session will take place at:{'\n'}
              <AddressLink>{formatAddress(clientAddress)}</AddressLink>
            </AddressBannerText>
            <AddressBannerSubtext>
              Please review the location details and use chat if you need to
              coordinate with the client.
            </AddressBannerSubtext>
          </AddressBanner>
        )}
        {/* Accept/Decline Buttons for Pending Requests */}
        {isPendingInPersonRequest && (
          <AcceptDeclineContainer>
            <Button
              variant="secondary"
              text="Decline"
              onPress={handleDeclineRequest}
              size="large"
              loading={isProcessingRequest}
              disabled={isProcessingRequest}
              customStyle={{
                width: isDesktop ? 150 : 140,
                height: 42,
                marginRight: 16,
              }}
              customLabelStyle={{
                fontSize: 14,
                fontWeight: 'bold',
                letterSpacing: 0.7,
              }}
            />
            <Button
              variant="primary"
              text="Accept"
              onPress={handleAcceptRequest}
              size="large"
              loading={isProcessingRequest}
              disabled={isProcessingRequest}
              customStyle={{
                width: isDesktop ? 150 : 140,
                height: 42,
              }}
              customLabelStyle={{
                fontSize: 14,
                fontWeight: 'bold',
                letterSpacing: 0.7,
              }}
            />
          </AcceptDeclineContainer>
        )}
        {!isPendingInPersonRequest && !isJoinable && !inPersonSession && (
          <ComeBackText status={consultation.status} />
        )}
        {!isPendingInPersonRequest && (
          <CancelButton
            onCancel={handleCancelPress}
            consultationDate={consultation.startTime}
            isCancelled={getIsConsultationCancelled(consultation.status)}
            isStylist={isStylist ?? false}
            isComplete={consultation.status === ConsultationStatus.Completed}
          />
        )}
        {!isPendingInPersonRequest && (
          <ActionButtons
            canJoin={inPersonAtEvent || inPersonSession ? false : isJoinable}
            handleJoinCall={handleJoinCall}
            handleGoToChat={handleGoToChat}
            handleViewDetails={() => {}}
            isChatButtonPrimaryStyle={
              (isStylist &&
                consultation.status === ConsultationStatus.Completed) ||
              (!isStylist && (inPersonAtEvent || inPersonSession))
            }
            customButtonWidth={isDesktop ? 528 : undefined}
            isJoining={inPersonAtEvent || inPersonSession ? false : isJoining}
            showEndButton={
              (inPersonAtEvent || inPersonSession) &&
              !!isStylist &&
              consultation.status === ConsultationStatus.InProgress
            }
            handleEndSession={handleEndSession}
          />
        )}
        <PaddingContainer>
          <StylistImage source={{ uri: otherUser?.profilePicUrl }} />

          <PricingContainer isStylist={isStylist ?? false}>
            {!isStylist && (
              <PricingInfo>
                <PriceText>
                  {formatCurrency(stylistRate ?? 0)}
                  /hour
                </PriceText>
                <TierText>{priceTierLabel}</TierText>
              </PricingInfo>
            )}
            <LinkRow>
              <TouchableOpacity onPress={handleViewProfile}>
                <ViewProfileText>View Profile</ViewProfileText>
              </TouchableOpacity>
              {isStylist && consultation.client?.id && (
                <ViewPrivateNotesLink
                  clientId={consultation.client.id}
                  style={{ marginLeft: 16 }}
                  showUnderline={true}
                />
              )}
            </LinkRow>
          </PricingContainer>
        </PaddingContainer>
        <Receipt
          items={receiptItems}
          totalPrice={formatCurrency(consultation.estimatedPrice)}
          notes={consultation.consultationDetails?.clientNotes ?? ''}
          isScrollable={false}
        />
        <BottomSpacing />
        <BackToTopButton scrollToTop={scrollToTop} />
      </Content>
      <Spacer height={49} />
      <CancelSessionModal
        otherUserName={`${otherUser?.firstName} ${otherUser?.lastName}`}
        consultationDate={consultation.startTime}
        isModalVisible={isCancelModalVisible}
        setIsModalVisible={setIsCancelModalVisible}
        consultationId={consultation.id ?? undefined}
        stylistId={consultation.stylist?.id ?? undefined}
        clientId={consultation.client?.id ?? undefined}
        closeParentModal={onClose}
        isOutsideRefundWindow={hoursUntilConsultation < 24}
        sessionPrice={consultation.estimatedPrice}
      />
      {(inPersonAtEvent || inPersonSession) && isStylist && (
        <InPersonCompletionModal
          isVisible={isInPersonCompletionModalVisible}
          setIsVisible={setIsInPersonCompletionModalVisible}
          consultation={consultation}
        />
      )}
    </ModalWrapper>
  );
};

const containerStyles = css({
  flex: 1,
  backgroundColor: sand,
  maxWidth: 697,
  maxHeight: 1247,
});

const Header = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: 23,
  paddingTop: 21,
});

const HeaderLeft = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
});

const Content = styled.View({
  flex: 1,
  alignItems: 'center',
});

const PaddingContainer = styled.View({
  paddingHorizontal: 23,
  marginBottom: 25,
});

const Title = styled(H3)({
  marginBottom: 16,
  textTransform: 'uppercase',
  textAlign: 'center',
});

const StylistImage = styled.Image({
  width: 335,
  height: 295,
  marginBottom: 21,
  borderRadius: 8,
});

const PricingContainer = styled.View<{ isStylist: boolean }>(
  ({ isStylist }) => ({
    flexDirection: 'row',
    justifyContent: isStylist ? 'center' : 'space-between',
  }),
);

const PricingInfo = styled.View({});

const PriceText = styled(BodyText)({
  fontSize: 20,
  fontWeight: '600',
});

const TierText = styled(BodyText)({
  fontSize: 12,
  color: subheadline,
  lineHeight: 14,
});

const ViewProfileText = styled(BodyText)({
  textDecorationLine: 'underline',
  fontWeight: 'bold',
  fontSize: 14,
});

const LinkRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
});

const BottomSpacing = styled.View({
  height: 49,
});

// Pending Request Styles
const PendingRequestBadge = styled(BodyText)({
  backgroundColor: accent,
  color: green,
  paddingVertical: 4,
  textTransform: 'uppercase',
  fontWeight: 'bold',
  fontSize: 10,
  width: 110,
  height: 23,
  textAlign: 'center',
  lineHeight: 15,
});

const AddressBanner = styled.View({
  backgroundColor: mint,
  paddingHorizontal: 23,
  paddingVertical: 16,
  marginBottom: 20,
  alignItems: 'center',
  width: '100%',
});

const AddressBannerText = styled(BaseText)({
  fontSize: 14,
  color: subheadline,
  marginBottom: 16,
  lineHeight: 20,
  textAlign: 'center',
});

const AddressLink = styled(BaseText)({
  fontSize: 14,
  color: subheadline,
  textDecorationLine: 'underline',
  fontWeight: 600,
  textAlign: 'center',
});

const AddressBannerSubtext = styled(BaseText)({
  fontSize: 14,
  color: subheadline,
  textAlign: 'center',
  lineHeight: 16,
});

const AcceptDeclineContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: 20,
  paddingHorizontal: 23,
});

export default SessionDetailView;
